import moment from "moment";
import { TDateOrNull } from "../input/DateInput";

export const formatDateToLocale = (dateString: string | Date | undefined, withTime = false) => {
	if (!dateString) return "No date";
	const date = new Date(dateString);

	if (isNaN(date.getTime())) {
		return "Failed date format";
	}

	if (withTime) {
		return date
			.toLocaleString("en-US", {
				year: "numeric",
				month: "numeric",
				day: "numeric",
				hour: "numeric",
				minute: "2-digit",
			})
			.replace(",", "");
	}

	const newDate = date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	});

	return newDate;
};

export const enum DateFormatsEnum {
	"MM/DD/YYYY" = "MM/DD/YYYY",
	"DD MMM" = "DD MMM",
	"MM/DD/YYYY hh:mm A" = "MM/DD/YYYY hh:mm A",
	"MM/DD/YYYY mm:ss" = "MM/DD/YYYY mm:ss",
	"MM/DD/YYYY | mm:ss a" = "MM/DD/YYYY | mm:ss a",
	"mm:ss" = "mm:ss",
	"dddd" = "dddd",
	"HH:MM AM/PM" = "hh:mm A",
	"MMM DD YYYY, h:mm A" = "MMM DD YYYY, h:mm A",
}
export const formatDate = (
	dateString: string | Date | undefined | null,
	format: DateFormatsEnum = DateFormatsEnum["MM/DD/YYYY"],
	returnValue?: string,
) => {
	if (!dateString) return returnValue ?? "--";
	return moment(dateString).format(format);
};

export const daysDifference = (targetDate: Date) => {
	const currentDate = new Date();
	const target = new Date(targetDate);

	const differenceInTime = target.getTime() - currentDate.getTime();
	return Math.ceil(differenceInTime / (1000 * 60 * 60 * 24));
};

export const toApiDateString = (value: string | Date | null | undefined): string | null => {
	if (!value) return null;

	if (typeof value === "string") {
		const parts = value.split("-");
		if (parts.length === 3 && parts[2].length === 4) {
			const [month, day, year] = parts;
			return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
		}
	}

	const parsed = new Date(value);
	if (isNaN(parsed.getTime())) return null;
	return formatDate(parsed, DateFormatsEnum["MM/DD/YYYY"]);
};

export const getDatePickerFormattedDate = (date: TDateOrNull, daysOffset = 0) => {
	if (!date) return "";

	const newDate = new Date(date?.getFullYear(), date?.getMonth(), date?.getDate() + daysOffset);
	return `${newDate?.getMonth() + 1}-${newDate?.getDate()}-${newDate?.getFullYear()}`;
};

export const isDateBeforeToday = (value: string | null | undefined, today = new Date()) => {
	if (!value) return false;

	const date = new Date(value);
	if (isNaN(date.getTime())) return false;

	const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
	return new Date(date.getFullYear(), date.getMonth(), date.getDate()) < startOfToday;
};

export function adjustDate(date: string | Date | undefined, days: number) {
	if (!date) return undefined;

	const newDate = new Date(date);
	newDate.setDate(newDate.getDate() + (days ?? 0));

	return newDate.toLocaleDateString();
}

export const schedulePriceformatTime = (date: Date) => {
	const estDateStr = date.toLocaleString("en-US", { timeZone: "America/New_York" });
	const estDate = new Date(estDateStr);

	const hours24 = estDate.getHours();
	const minutes = estDate.getMinutes();

	const abbreviation = hours24 >= 12 ? "PM" : "AM";
	let hour12 = hours24 % 12;
	if (hour12 === 0) hour12 = 12;

	return {
		hour: hour12,
		minute: minutes,
		abbreviation,
	};
};

export const schedulePriceParseEventTime = (eventTimeStr: string) => {
	const [time, meridian] = eventTimeStr.split(" ");
	const [hourStr, minuteStr] = time.split(":");

	let hour = parseInt(hourStr, 10);
	const minute = parseInt(minuteStr, 10);
	const abbreviation = meridian;

	if (abbreviation === "PM" && hour !== 12) {
		hour += 12;
	} else if (abbreviation === "AM" && hour === 12) {
		hour = 0;
	}

	return {
		hour12: hour % 12 === 0 ? 12 : hour % 12,
		minute,
		abbreviation,
	};
};
